import React, { useMemo, useState } from "react";

// =============================================================
// InitTreeModal — SA-only. Creates the Alpha node with an
// optional initial occupant (searchable). Falls back to a Vacant
// Alpha when no member is chosen.
// =============================================================
export default function InitTreeModal({ members, onConfirm, onClose, submitting }) {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (members || []).filter(m => {
      if (!term) return true;
      const name = ((m.firstName || "") + " " + (m.lastName || "")).toLowerCase();
      const num = String(m.memberNumber || "");
      return name.includes(term) || num.includes(term.replace(/^#/, ""));
    }).slice(0, 40);
  }, [q, members]);

  return (
    <div className="modal-overlay" data-testid="init-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Initialize Alpha Node</div>
          <button className="modal-close" onClick={onClose} data-testid="init-close" aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <div style={{ padding: 12, background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 10, marginBottom: 14, fontSize: 12, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", lineHeight: 1.55 }}>
            Pick the member who will occupy the Alpha slot (the root of the tree). You can also leave this blank to create Alpha as a vacant slot and assign someone later.
          </div>
          <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Search member (recommended)</label>
          <input
            data-testid="init-search-input"
            autoFocus
            value={q}
            onChange={(e) => { setQ(e.target.value); setSelectedId(null); }}
            placeholder="e.g. Pastor Emmanuel, 0000047"
            className="form-input"
            style={{ width: "100%", marginTop: 6, marginBottom: 12 }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
            {results.map(m => {
              const isSel = selectedId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`init-result-${m.id}`}
                  onClick={() => setSelectedId(m.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    borderRadius: 10, cursor: "pointer",
                    background: isSel ? "color-mix(in srgb, var(--kc-accent) 15%, transparent)" : "transparent",
                    border: `1px solid ${isSel ? "var(--kc-accent)" : "var(--kc-sub)"}`,
                    textAlign: "left", color: "inherit", font: "inherit",
                  }}
                >
                  {m.photo ? (
                    <img src={m.photo} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>
                      {(m.firstName?.[0] || "?") + (m.lastName?.[0] || "")}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kc-text)" }}>{(m.firstName || "") + " " + (m.lastName || "")}</div>
                    <div style={{ fontSize: 11, color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>#{m.memberNumber || "—"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} data-testid="init-cancel">Cancel</button>
          <button type="button" className="btn btn-outline btn-sm" data-testid="init-vacant" disabled={submitting} onClick={() => onConfirm(null)}>Create Vacant</button>
          <button
            type="button"
            className="btn btn-gold btn-sm"
            data-testid="init-confirm"
            disabled={!selectedId || submitting}
            onClick={() => onConfirm(selectedId)}
          >{submitting ? "Initializing…" : "Initialize Alpha"}</button>
        </div>
      </div>
    </div>
  );
}
